import React, { useEffect, useRef, useState, useCallback } from "react";
import UserChatBox from "./UserChatBox";
import AIChatBox from "./AIChatBox";
import { useLocalState } from "../../contexts/LocalStateProvider";
import { useAudioPlayer } from "../../contexts/AudioPlayerContext";
import { getCookie } from "../../utils/cookieUtils";
import axios from "axios";
import ChatBoxLoader from "./ChatBoxLoader";
import apiService from "../../api/apiService";
import Toast from "../generic_components/Toast";
import { setUserData } from "../../store/reducers/userSlice";
import { useDispatch } from "react-redux";
import { usePostHog } from "posthog-js/react";
import {
  setSubscriptionModal,
  setSubscriptionTypeModal,
  setRedirectAfterSelectedPlan,
} from "../../store/reducers/subscriptionSlice";
import { useGameContext } from "../../contexts/GameContext";
import StreakModal from "./StreakModal";
import TaskCompletion from "./TaskCompletion";
import * as Sentry from "@sentry/react"
import { getPartitionedLocalStorage } from "../../utils/tokenExtraction";

const ChatInterface = ({ initialMessages = [], projectId }) => {
  const posthog = usePostHog();
  const { localState, updateLocalState } = useLocalState();
  const [messages, setMessages] = useState(initialMessages);
  const [lastUserInput, setLastUserInput] = useState(null);
  const [isRegeneration, setIsRegeneration] = useState(false);
  const [regeneratingResponseId, setRegeneratingResponseId] = useState(null);
  const [beforeResponseLoader, setBeforeResponseLoader] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [pageWidth, setPageWidth] = useState(window.innerWidth);
  const [scrollHeight, setScrollHeight] = useState(0);
  const [beforeRegenerateLoaderId, setBeforeRegenerateLoaderId] =
    useState(null);
  const [stopGeneration, setStopGeneration] = useState(false);
  const messagesEndRef = useRef(null);
  const {
    setIsBuffering,
    setStreamingSrc,
    setAiResponseId,
    setDiscoverId,
    stopAudio,
    setImageSrc,
  } = useAudioPlayer();
  const [loadingResponseId, setLoadingResponseId] = useState(null);
  const [toastType, setToastType] = useState("info");
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const dispatch = useDispatch();
  const [projectName, setProjectName] = useState(null);
  const [generationCount, setGenerationCount] = useState(0);
  const streamingControllerRef = useRef(null);
  const stopGenerationRef = useRef(false);
  const abortControllerRef = useRef(null);

  const { gamificationData, fetchGamificationData, setGamificationData } = useGameContext();
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [streakInfo, setStreakInfo] = useState(null);
  const [triggerTaskModal, setTriggerTaskModal] = useState(false);
  const [versionHistoryPersistent, setVersionHistoryPersistent] = useState([]);

  const stoppedResponseIdsRef = useRef(new Set());

  useEffect(() => {
    stopGenerationRef.current = stopGeneration;
  }, [stopGeneration]);

  // Inside useEffect that handles new messages
  useEffect(() => {
    if (localState.newMessage && !isRegeneration) {
      setGenerationCount((prev) => prev + 1);
    }
  }, [localState.newMessage, isRegeneration]);

  useEffect(() => {
    // Count existing AI messages with audio on component mount
    const existingAudioMessages = messages.filter(
      (msg) => msg.sender === "Wubble" && msg.audio
    );
    setGenerationCount(existingAudioMessages.length);
  }, []);






  useEffect(() => {
    // Check for the last AI response from Wubble
    const lastAiResponse = messages
      .slice()
      .reverse()
      .find((msg) => msg.sender === "Wubble");

    if (lastAiResponse && lastAiResponse.next_prompt_suggestion) {
      // Update local state with the next prompt suggestion
      updateLocalState(
        "nextPromptSuggestion",
        lastAiResponse.next_prompt_suggestion
      );
    }
  }, [messages]);

  useEffect(() => {
    const fetchProjectName = async () => {
      try {
        const response = await apiService.handleProtectedRoute(
          "fetchProjectName",
          {
            projectId: projectId,
          }
        );
        if (response.success) {
          setProjectName(response.projectName);
        }
      } catch (error) {
        console.error("Error fetching project name:", error);
        Sentry.captureException(error);
      }
    };

    if (projectId) {
      fetchProjectName();
    }
  }, [projectId]);

  const userId = getCookie("user_id") ||getPartitionedLocalStorage("user_id");

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  const filterAIResponsesWithAudio = (messages) => {
    return messages.filter((msg) => msg.sender === "Wubble" && msg.audio);
  };

  // const scrollToMessage = (responseId) => {
  //   const messageElement = document.getElementById(responseId);
  //   if (messageElement) {
  //     messageElement.scrollIntoView({ behavior: "smooth" });
  //     updateLocalState("scrollToResponseId", null);
  //   }
  // };

  useEffect(() => {
    if (localState.scrollToUserInput) {
      const userInputElement = document.getElementById(
        `user-input-${localState.scrollToUserInput}`
      );
      if (userInputElement) {
        userInputElement.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [localState.scrollToUserInput]);

  useEffect(() => {
    localState.isAudioPlaying
      ? setIsAudioPlaying(true)
      : setIsAudioPlaying(false);
  }, [localState.isAudioPlaying]);

  useEffect(() => {
    const fetchProjectSetByUser = async () => {
      try {
        const response = await apiService.sendRequest("fetchProjectSetByUser", {
          project_id: projectId,
        });
        if (response.success) {
          updateLocalState("projectNameSetByUser", response.set_by_user);
        }
      } catch (error) {
        console.error("Error fetching project set_by_user status:", error);
        Sentry.captureException(error);
      }
    };

    fetchProjectSetByUser();
  }, [projectId]);

  // useEffect(() => {
  //   if (localState.scrollToResponseId) {
  //     scrollToMessage(localState.scrollToResponseId);
  //   }
  // }, [localState.scrollToResponseId]);

useEffect(() => {
  const aiResponsesWithAudio = filterAIResponsesWithAudio(messages);
  const groupedVersionHistory = groupVersionHistory(aiResponsesWithAudio);

  // Merge previous selections into the new versionHistory
  const selections = localState.trackSelections || {};
  const mergedVersionHistory = groupedVersionHistory.map(group => {
    // For latest
    let latest = { ...group.latest };
    if (selections[latest.ai_response_id] !== undefined) {
      latest.selected_track_id = selections[latest.ai_response_id];
    }
    // For versions array
    let versions = group.versions.map(v => {
      if (selections[v.ai_response_id] !== undefined) {
        return { ...v, selected_track_id: selections[v.ai_response_id] };
      }
      return v;
    });
    return { ...group, latest, versions };
  });

  updateLocalState("versionHistory", mergedVersionHistory);
}, [messages, localState.newMessage, localState.trackSelections]);

  // const groupVersionHistory = (responses) => {
  //   const generationGroups = {};
  //   let currentGroupId = null;

  //   responses.forEach((response, index) => {
  //     if (response.responseType === "generation") {
  //       if (response.regenerated && response.parent_response_id) {
  //         // Handle regenerated responses by adding them to parent's variations
  //         const parentId = response.parent_response_id;
  //         if (!generationGroups[parentId]) {
  //           generationGroups[parentId] = {
  //             original: null,
  //             variations: [],
  //           };
  //         }
  //         generationGroups[parentId].variations.push(response);
  //       } else {
  //         // Handle normal generations
  //         currentGroupId = response.parent_response_id || response.id;
  //         generationGroups[currentGroupId] = {
  //           original: response,
  //           variations: [],
  //         };
  //       }
  //     } else if (response.responseType === "extension") {
  //       // Handle extensions
  //       if (currentGroupId && generationGroups[currentGroupId]) {
  //         generationGroups[currentGroupId].variations.push(response);
  //       }
  //     } else if (response.responseType === "edit") {
  //       // Handle edits
  //       const previousResponse = responses[index - 1];
  //       if (previousResponse) {
  //         const groupId = previousResponse.parent_response_id || currentGroupId;
  //         if (groupId && generationGroups[groupId]) {
  //           generationGroups[groupId].variations.push(response);
  //         } else if (groupId) {
  //           generationGroups[groupId] = {
  //             original: null,
  //             variations: [response],
  //           };
  //         }
  //       }
  //     }
  //   });

  //   return Object.values(generationGroups)
  //     .filter((group) => group.original || group.variations.length > 0)
  //     .map((group) => {
  //       const sortedVariations = group.variations.sort(
  //         (a, b) => new Date(b.created_at) - new Date(a.created_at)
  //       );

  //       return {
  //         latest: sortedVariations[0] || group.original,
  //         hasVariations: group.variations.length > 0,
  //         versions: group.original
  //           ? [...sortedVariations, group.original]
  //           : sortedVariations,
  //       };
  //     })
  //     .sort(
  //       (a, b) => new Date(b.latest.created_at) - new Date(a.latest.created_at)
  //     );
  // };

  const groupVersionHistory = (responses) => {
    const generationGroups = {};
    let currentGroupId = null;

    responses.forEach((response, index) => {
      if (response.is_error) return;
      if (response.responseType === "generation") {
        if (response.regenerated && response.parent_response_id) {
          // Handle regenerated responses
          const parentId = response.parent_response_id;
          if (!generationGroups[parentId]) {
            generationGroups[parentId] = {
              original: null,
              variations: [],
            };
          }

          // If original is null, set current response as original
          if (!generationGroups[parentId].original) {
            generationGroups[parentId].original = response;
          } else {
            // Else, add to variations
            generationGroups[parentId].variations.push(response);
          }
        } else {
          // Handle normal generations
          currentGroupId =
            response.parent_response_id || response.ai_response_id;
          generationGroups[currentGroupId] = {
            original: response,
            variations: [],
          };
        }
      } else if (
        response.responseType === "edit" ||
        response.responseType === "extension"
      ) {
        // For edit or extension responses, add them to the current group
        if (currentGroupId && generationGroups[currentGroupId]) {
          generationGroups[currentGroupId].variations.push(response);
        } else {
          let foundParent = false;

          // If no current group, look back for the last generation response
          for (let i = index - 1; i >= 0; i--) {
            const prevResponse = responses[i];
            if (prevResponse.responseType === "generation") {
              const groupId =
                prevResponse.parent_response_id || prevResponse.ai_response_id;
              if (generationGroups[groupId]) {
                generationGroups[groupId].variations.push(response);
                foundParent = true;
                break;
              }
            }
          }

          // If no parent generation is found, create a standalone group for the extension
          if (!foundParent && response.responseType === "extension") {
            const standaloneGroupId = response.ai_response_id;
            generationGroups[standaloneGroupId] = {
              original: response, // Treat the extension as the original
              variations: [], // No variations for standalone extensions
              isStandalone: true, // Flag to indicate this is a standalone entry
            };
          }
        }
      } else {
        // Handle other response types if necessary
      }
    });

    return (
      Object.values(generationGroups)
        .filter((group) => group.original || group.variations.length > 0)
        .map((group) => {
          // Sort variations with latest first
          const sortedVariations = group.variations.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          );

          // Build the versions array with latest first, including the original at the end
          const versions =
            sortedVariations.length > 0
              ? [...sortedVariations, group.original].filter(
                (v) => v !== null && v !== undefined
              )
              : [group.original].filter((v) => v !== null && v !== undefined);

          // Get the latest response in the group
          const latest = versions[0];

          return {
            latest: latest,
            hasVariations: group.variations.length > 0,
            versions: versions,
          };
        })
        // Sort groups by the created_at of the latest response, latest first
        .sort(
          (a, b) =>
            new Date(b.latest.created_at) - new Date(a.latest.created_at)
        )
    );
  };

  const formatTime = (durationInSeconds) => {
    const minutes = Math.floor(durationInSeconds / 60);
    const seconds = Math.floor(durationInSeconds % 60);
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  useEffect(() => {
    if (!localState.scrollToAiResponse) {
      scrollToBottom();
    }
  }, [messages, isRegeneration, beforeResponseLoader, localState.newMessage]);

  useEffect(() => {
    const handleResize = () => {
      setPageWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    if (localState.textInputHeight > 24 && localState.textInputHeight < 48) {
      setScrollHeight(localState.textInputHeight - 16);
    }
    else if (localState.textInputHeight >= 48 && localState.textInputHeight < 72) {
      setScrollHeight(localState.textInputHeight - 32);
    }
    else if (localState.textInputHeight >= 72 && localState.textInputHeight < 90) {
      setScrollHeight(localState.textInputHeight - 42);
    }
    else if (localState.textInputHeight >= 90) {
      setScrollHeight(90 - 48);
    }
    else setScrollHeight(0);
  }, [localState.textInputHeight]);

  useEffect(() => {
    if (
      localState.newMessage &&
      !isRegeneration &&
      localState.newMessage?.get("project_id") === projectId
    ) {
      const formData = localState.newMessage;
      const messageObject = {};

      // Extract data from FormData
      formData.forEach((value, key) => {
        if (key === "file_name" || key === "mime_type") {
          // Convert the value (which should be a File or Blob) to a file object
          // const file = formData.get("media");

          messageObject.file = {
            name: formData.get("file_name"),
            type: formData.get("mime_type"),
            // content: file, // Store the actual file object
          };
        } else if (key === "message_id") {
          messageObject.id = value;
        } else if (key === "sentAt") {
          messageObject.sentAt = value;
        } else if (key === "prompt") {
          messageObject.prompt = value;
        } else {
          messageObject[key] = value;
        }
      });

      // Add additional fields to the message object
      messageObject.sender = "User";
      messageObject.text = messageObject.prompt || "";
      messageObject.avatar = "https://via.placeholder.com/50";

      // Avoid adding duplicate messages
      if (!messages.some((msg) => msg.id === Number(messageObject.id))) {
        // console.log("Adding new message:", messageObject);

        setMessages((prevMessages) => [...prevMessages, messageObject]);
        scrollToBottom();
      } else {
        // console.log("Duplicate message detected:", messageObject);
      }

      // console.log(messages);

      // Update last user input and reset new message
      setLastUserInput(messageObject);
      updateLocalState("newMessage", null);

      // Generate requestId
      const requestId = `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 11)}`;

      // Append requestId to the existing FormData
      formData.append("requestId", requestId);
      setTimeout(() => {
        setBeforeResponseLoader(true);
      }, 300);
      updateLocalState("responseInProgress", true);
      // Send AI response request with updated FormData
      sendAiResponseRequest(formData);
    }
  }, [localState.newMessage, isRegeneration]);

  useEffect(() => {
    // If lastUserInput is null, retrieve it from the last message in messages
    if (!lastUserInput) {
      const lastUserMessage = messages.findLast((msg) => msg.sender === "User");
      // console.log("lastUserMessage:", lastUserMessage);

      if (lastUserMessage) {
        const populatedLastUserInput = {
          ...lastUserMessage,
          user_id: userId, // Get userId from the cookie
          project_id: projectId, // Get projectId from props
          prompt: lastUserMessage.text, // Use text as the prompt
          userInput: {
            image_bytes: null, // Default to null
            audio_bytes: null, // Default to null
            video_bytes: null, // Default to null
          },
        };
        setLastUserInput(populatedLastUserInput);
      }
    }
  }, [messages, lastUserInput]);

  useEffect(() => {
    const lastMessageWithAudio = [...messages]
      .reverse()
      .find((msg) => msg.audio);

    if (lastMessageWithAudio && !lastMessageWithAudio.is_error) {
      const detailsData = {
        name: lastMessageWithAudio.audio.name,
        description: lastMessageWithAudio.musicDescription,
        coverImage: lastMessageWithAudio.avatar,
        duration: lastMessageWithAudio.audio.duration,
        audioSrc: lastMessageWithAudio.audio.src,
        ai_response_id: lastMessageWithAudio.ai_response_id,
        projectId: lastMessageWithAudio.projectId,
        trackId: lastMessageWithAudio?.trackId
      };

      updateLocalState("currentSongDetails", detailsData);
    }

    // Cleanup function that runs when component unmounts
    return () => {
      updateLocalState("stopGeneration", false);
      setBeforeResponseLoader(false);
      setLoadingResponseId(null);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (streamingControllerRef.current) {
        streamingControllerRef.current.abort();
        streamingControllerRef.current = null;
      }
    };
  }, []);


  useEffect(() => {
    if (localState.stopGeneration) {
      const userId =
        localState.userProfileData?.user_id || getCookie("user_id") || getPartitionedLocalStorage("user_id"); 
      const reqId = localState.currentRequestId || currentRequestId;
      const projId = projectId; 
      const msgId = lastUserInput?.id;
      const wasBeforeResponseLoaderActive = beforeResponseLoader; // Capture loader state

      (async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stop-generation`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`,
                "Content-Type": "application/json"
              },
              body: JSON.stringify({
                user_id: userId,
                project_id: projId,
                requestId: reqId,
                message_id: msgId || null,
              }), 
            }
          );
          const data = await response.json();
          const stoppedAiResponseIdFromAPI = data?.ai_response_id;

          if (data.success) {
            fetchUserData(); 
            updateLocalState("generatedRequestAudio", false);
            
            if (wasBeforeResponseLoaderActive) {
              const stopMessage = {
                sender: "Wubble",
                text: "You have stopped the generation! Re-enter prompt to start generating again.",
                ai_response_id: stoppedAiResponseIdFromAPI,
                responseType: "stopped",
                created_at: new Date().toISOString(),
                projectId: projId,
                isStopped: true,
              };
              setMessages((prevMessages) => [...prevMessages, stopMessage]);
              setBeforeResponseLoader(false); // Hide loader
            } else {
              setMessages((prev) =>
                prev.map((msg) => {
                  if (
                    msg.sender === "Wubble" &&
                    Number(msg.ai_response_id) === Number(stoppedAiResponseIdFromAPI)
                  ) {
                    return {
                      ...msg,
                      isStopped: true,
                      text: "You have stopped the generation! Re-enter prompt to start generating again",
                      audio: null, // Clear partially streamed audio
                    };
                  }
                  return msg;
                })
              );
            }
            updateLocalState("responseInProgress", false);
          } else {
            console.error("Failed to stop generation:", data.message);
            updateLocalState("responseInProgress", false);
            if (wasBeforeResponseLoaderActive) setBeforeResponseLoader(false);
          }
        } catch (err) {
          console.error("Error stopping generation:", err);
          Sentry.captureException(err);
          updateLocalState("responseInProgress", false);
          if (wasBeforeResponseLoaderActive) setBeforeResponseLoader(false);
        }

        if (streamingControllerRef.current) {
          streamingControllerRef.current.abort();
          streamingControllerRef.current = null; 
        }

        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null; 
        }
        
        stopAudio();
        updateLocalState("currentRequestId", null);
        updateLocalState("stopGeneration", false);
      })();
    }
  }, [localState.stopGeneration]);

  const fetchUserData = async () => {
    try {
      const response = await apiService.handleProtectedRoute(
        localState.isTeamMember ? "fetchTeamUserProfile" : "fetchUserProfile",
        {}
      );
      updateLocalState("userProfileData", response);
    } catch (error) {
      console.error("Error fetching user data:", error);
      Sentry.captureException(error);
    }
  };

  const fetchCoverImage = async (
    requestId,
    aiResponseId,
    aiResponseType,
    projectId,
    currentSongData
  ) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/fetch-cover-image`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            requestId,
            aiResponseId,
            aiResponseType,
            projectId,
          }),
        }
      );
      const data = await response.json();

      if (data.success) {
        setImageSrc(data.coverImageUrl);

        const updatedSongData = currentSongData;

        updatedSongData.coverImage = data.coverImageUrl;

        updateLocalState("currentSongDetails", updatedSongData);

        setMessages((prevMessages) =>
          prevMessages.map((msg) => {
            if (msg.ai_response_id === aiResponseId) {
              return {
                ...msg,
                avatar: data.coverImageUrl, // Set the cover image URL
                audio: {
                  ...msg.audio, // Keep existing audio properties
                  cover: data.coverImageUrl, // Add the audio cover image
                },
              };
            }
            return msg;
          })
        );

        updateLocalState("audioImgSrc", data.coverImageUrl);

        // Update cachedProjects state top reflect the new cover image
        const updatedCachedProjects = localState.cachedProjects.map(
          (project) => {
            if (project.project_id === msg?.projectId) {
              return {
                ...project,
                cover_image_name: data.coverImageUrl, // Update the cover image URL
              };
            }
            return project;
          }
        );

        // Only update state if the map operation was successful
        if (updatedCachedProjects) {
          updateLocalState("cachedProjects", updatedCachedProjects);
        }
      }
    } catch (error) {
      console.error("Error fetching cover image:", error);
      Sentry.captureException(error);
    }
  };

  const updateUserStreak = async () => {
    try {
      const token = getCookie("token") || getPartitionedLocalStorage("token");

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/update-streak`,
        {},
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const lastStreak = gamificationData.streak
      console.log(gamificationData.ai_response_count, "AI response count")

      console.log("Streak :", response.data.streak, "Last Streak :", lastStreak);
      if (response.data.streak > lastStreak) {
        console.log("Your streak has been updated");
        setStreakInfo(response.data);
        setShowStreakModal(true); // show modal

      }

    } catch (error) {
      console.error("Error updating streak:", error);
      Sentry.captureException(error);
    }
  };



  const taskUpdate = async (userPrompt, fileInfo) => {
    const token = getCookie("token") || getPartitionedLocalStorage("token");
    if (!token || !gamificationData) return;

    console.log("Reached Task Update");

    // Get the media type and full MIME type from passed fileInfo
    const mediaType = fileInfo?.type?.split("/")[0];
    const fullMediaType = fileInfo?.type;
    const fullMedia = fileInfo; // fileInfo itself is the file object if provided
    console.log("Media Type:", mediaType, "Full MIME Type:", fullMediaType, "fullMedia:", fullMedia, "userPrompt:", userPrompt);

    // Define accepted media types


    try {
      // Check for first AI response (audio)
      if (gamificationData.ai_response_count === 0 || gamificationData.tasks.find(task => task.task_key === "generate_track")?.is_completed === false) {
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_ENDPOINT}/api/complete-task`,
          { taskKey: "generate_track" },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.status === "completed") {
          console.log("Generated track task completed");
          setTriggerTaskModal(true);
          setTimeout(() => setTriggerTaskModal(false), 3000);
          await fetchGamificationData();
        }
      }

      // Check for customize track task
      if (gamificationData.ai_response_count >= 1 && gamificationData.tasks.find(task => task.task_key === "customize_track")?.is_completed === false) {
        console.log("Attempting to complete customize_track task");
        const response = await axios.post(
          `${process.env.REACT_APP_SERVER_ENDPOINT}/api/complete-task`,
          { taskKey: "customize_track" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.status === "completed") {
          console.log("Customized track task completed");
          setTriggerTaskModal(true);
          setTimeout(() => setTriggerTaskModal(false), 3000);
          await fetchGamificationData();
        }
      }

      // Removed file type checks for image/video here, assuming they are handled elsewhere (e.g., CustomInput)

      // Fetch latest data just in case, though specific updates above already do this
      // await fetchGamificationData();
    } catch (error) {
      console.error("Error updating tasks:", error);
      Sentry.captureException(error);
    }
  };

  //helper function in case "you have stopped generation" message gets repeated.
const handleStopMessage = (aiResponseId) => {
  if (stoppedResponseIdsRef.current.has(aiResponseId)) return;
  stoppedResponseIdsRef.current.add(aiResponseId);

  // Update existing message
  setMessages((prev) =>
    prev.map((msg) =>
      Number(msg.ai_response_id) === Number(aiResponseId)
        ? {
            ...msg,
            isStopped: true,
            text: "You have stopped the generation! Re-enter prompt to start generating again",
          }
        : msg
    )
  );

  if (localState?.versionHistory) {
    const updatedVersionHistory = localState?.versionHistory.map((group) => ({
      ...group,
      versions: group.versions.map((v) =>
        v.ai_response_id === aiResponseId
          ? {
              ...v,
              isStopped: true,
              text: "You have stopped the generation! Re-enter prompt to start generating again",
            }
          : v
      ),
    }));

    updateLocalState("versionHistory", updatedVersionHistory);
  }

  updateLocalState("currentSongDetails", null);
  stopAudio();
};



  const sendAiResponseRequest = (requestPayload) => {
    // Create a new AbortController for the request
    const token = getCookie("token") || getPartitionedLocalStorage("token");
    const controller = new AbortController();
    const signal = controller.signal;
    abortControllerRef.current = controller;

    const requestId = requestPayload.get("requestId");
    setCurrentRequestId(requestId);
    updateLocalState("projectId", projectId);
    const regeneratingResponseIdCurrent = requestPayload.get(
      "regeneratingResponseId"
    );
    updateLocalState("newSongGenerated", true);


    axios
      .post(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/ai-response`,
        requestPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          signal,
        }
      )
      .then(async (response) => {
        const data = response.data;

        // ---- ADD THIS STOP CHECK BEFORE inserting new message ----
        if (localState.stopGeneration) {
          console.log(
            "User already stopped generation. Skipping partial data."
          );
          return; // So we do not call setMessages(...) with the new data
        }

        fetchUserData();
        updateUserStreak()
        // Pass relevant info directly to taskUpdate from requestPayload
        const userPromptForTask = requestPayload.get("prompt") || "";
        const fileInfoForTask = requestPayload.get("media") ? {
          type: requestPayload.get("mime_type"),
          name: requestPayload.get("file_name")
          // Note: we don't pass the actual file content here unless needed by taskUpdate
        } : null;
        taskUpdate();

        // console.log("Response data:", data);

        if (data.success) {
          // getting track count in response as 3 for multigeneration

          //handling multiple tracks


          let aiResponse = {
            sender: "Wubble",
            text: data.data.generatedText,
            ai_response_id: data.data?.responseId,
            parent_response_id: data.data?.parentResponseId,
            regenerated:
              data.data?.regenerated ||
              requestPayload.get("regeneration") === true,
            // Ensure the requestId for the AI message object is the one from this specific operation
            requestId: data.data?.requestId || requestId, // requestId here is from requestPayload.get("requestId")
          };

          // Add this after setting aiResponse
          if (data.data?.next_prompt_suggestion) {
            updateLocalState(
              "nextPromptSuggestion",
              data.data.next_prompt_suggestion
            );
          } else {
            updateLocalState("nextPromptSuggestion", null);
          }

          setBeforeResponseLoader(false);
          setBeforeRegenerateLoaderId(null);


          if (
            data?.data?.songTitle ||
            data?.data?.extendAudio ||
            data?.data?.responseType !== "text"
          ) {
            const trackCount = data.data?.trackCount || 1;
            const initialTrackId = 0; // Assume track 0 is the primary for initial display
            const coverImage = data.data?.coverImage?.data
              ? `data:image/png;base64,${data.data.coverImage?.data}`
              : "/project-images/Album_Art_6.webp";

            // Construct the initial AI response object with audio details for track 0
            aiResponse = {
              ...aiResponse,
              avatar: coverImage,
              audio: {
                name: data.data?.songTitle,
                duration: formatTime(data.data?.music_duration),
                // We might not even need the src here initially, AIChatBox can construct it
                // src: `${process.env.REACT_APP_SERVER_ENDPOINT}/api/audio-stream/${requestId}/${initialTrackId}`,
                cover: coverImage,
              },
              // actions: ["Extend duration", "Change genre"], // Keep or remove based on design
              musicCaption: data.data?.musicCaption,
              coverDescription: data.data?.coverDescription,
              projectId: data.data?.projectId,
              musicDescription: data.data.musicDescription,
              responseType: data.data?.responseType,
              created_at: new Date().toISOString(),
              trackCount: trackCount, // Pass track count to AIChatBox
              // trackId: initialTrackId, // No need to pass initial track ID? AIChatBox manages tracks.
            };

            // Set loadingResponseId to trigger loading state in AIChatBox
            setLoadingResponseId(aiResponse.ai_response_id);

            // Fetch cover image separately if needed (logic might move to AIChatBox too)
            fetchCoverImage(
              requestId,
              aiResponse.ai_response_id,
              data?.data?.responseType,
              projectId,
              { // Pass potential details for update if needed
                name: aiResponse.audio.name,
                description: aiResponse.musicDescription,
                coverImage: aiResponse.avatar,
                duration: aiResponse.audio.duration,
                ai_response_id: aiResponse.ai_response_id,
                projectId: aiResponse.projectId,
              }
            );

            updateLocalState("currentRequestId", requestId);
            updateLocalState("generatedRequestAudio", false); // Reset this?
            updateLocalState("ai_response_id", data.data?.responseId);
            updateLocalState("audioImgSrc", coverImage);

            // Update currentSongDetails with initial info (optional, AIChatBox might handle this on play)
            const detailsData = {
              name: aiResponse.audio.name,
              description: aiResponse.musicDescription,
              coverImage: aiResponse.avatar,
              duration: aiResponse.audio.duration,
              // audioSrc: aiResponse.audio.src, // Source is dynamic per track
              ai_response_id: aiResponse.ai_response_id,
              projectId: aiResponse.projectId,
              responseType: aiResponse.responseType,
              created_at: new Date().toISOString(),
            };
            updateLocalState("currentSongDetails", detailsData);
            
            posthog.capture("Music generation initiated", {
                is_user_logged_in: true,
                // project_id: projectId,
                // response_type: aiResponse.responseType,
                // has_audio: true
            }); // Changed event name

          } else {
            updateLocalState("responseInProgress", false);
            setLoadingResponseId(null); // Ensure loading ID is cleared for text responses
          }

          // console.log("AI Response after:", aiResponse);
          // console.log("Regenration response id", regeneratingResponseIdCurrent);

          setMessages((prevMessages) => {
            let updatedMessages;

            if (regeneratingResponseIdCurrent) {
              // Find if we're regenerating from an error message
              const isErrorRegeneration = prevMessages.some(
                (msg) =>
                  Number(msg.ai_response_id) ==
                  Number(regeneratingResponseIdCurrent) && msg.is_error
              );

              if (isErrorRegeneration) {
                // Replace the error message with the successful response
                updatedMessages = prevMessages.map((msg) =>
                  Number(msg.ai_response_id) ===
                    Number(regeneratingResponseIdCurrent)
                    ? aiResponse
                    : msg
                );
              } else {
                // Regular regeneration - add as new message
                updatedMessages = [...prevMessages, aiResponse];
              }
            } else {
              // Normal flow - add new message
              updatedMessages = [...prevMessages, aiResponse];
            }

            // Group AI messages while keeping user messages intact
            const groupedMessages = groupRelatedMessages(updatedMessages);

            // Flatten the grouped messages array and return for state update
            return groupedMessages.flat();
          });

          setBeforeResponseLoader(false);

          scrollToBottom();

          if (data?.data?.songTitle && !localState.projectNameSetByUser) {
            const updateProjectNamePayload = {
              projectId,
              project_name: data?.data?.songTitle,
              set_by_user: false,
            };
            await apiService.sendRequest(
              "updateProjectName",
              updateProjectNamePayload
            );
            updateLocalState("projectName", data?.data?.songTitle);
            updateLocalState("projectNameUpdated", true);
          }
        } else {
          console.error("Error: AI response generation failed.");
        }
        if (isRegeneration) {
          setIsRegeneration(false);
          setRegeneratingResponseId(null);
        }
        setCurrentRequestId(null);
      })
      .catch(async (error) => {
        posthog.capture("Model Failure", {
          is_user_logged_in: true
        });
        Sentry.captureException(error);
        if (stopGenerationRef.current) {
          console.log("AI generation stopped   sdf");

          // Overwrite partial message in the UI
          setMessages((prev) =>
            prev.map((msg) => {
              if (
                msg.sender === "Wubble" &&
                Number(msg.ai_response_id) === Number(localState.ai_response_id)
              ) {
                return {
                  ...msg,
                  isStopped: true,
                  text: "You have stopped the generation! Re-enter prompt to start generating again",
                };
              }
              return msg;
            })
          );

          // setBeforeResponseLoader(false);
          updateLocalState("responseInProgress", false);
        }

        if (error.name === "AbortError") {
          console.log("AI generation stopped for abort error");

          setBeforeResponseLoader(false);
          updateLocalState("responseInProgress", false);

          return;
        }

        console.error("Error sending AI response request:", error);
        if (error.response?.status === 504 || error.response?.status === 500) {
          const errorResponse = error.response?.data?.data;
          await new Promise((resolve) => setTimeout(resolve, 1000));

          if (
            errorResponse?.responseId ||
            errorResponse?.regeneratingResponseId
          ) {
            if (isRegeneration) {
              setMessages((prevMessages) =>
                prevMessages.map((msg) =>
                  msg.ai_response_id === errorResponse.regeneratingResponseId
                    ? {
                      ...msg,
                      text: errorResponse.generatedText,
                      is_error: true,
                      error_message: errorResponse.error_message,
                    }
                    : msg
                )
              );
              setBeforeRegenerateLoaderId(null);
            } else {
              const aiErrorResponse = {
                sender: "Wubble",
                text: errorResponse.generatedText,
                ai_response_id: errorResponse.responseId,
                is_error: true,
                error_message: errorResponse.error_message,
              };
              setMessages((prevMessages) => [...prevMessages, aiErrorResponse]);
              setBeforeRegenerateLoaderId(null);
            }
            scrollToBottom();
          } else {
            setToastMessage(
              "The server is currently unavailable. Please try again later."
            );
          }
        }
        setBeforeResponseLoader(false);
        setBeforeRegenerateLoaderId(null);
        updateLocalState("responseInProgress", false);

        if (isRegeneration) {
          setIsRegeneration(false);
          setRegeneratingResponseId(null);
        }

        setCurrentRequestId(null);
      });

    updateLocalState("newMessage", null);
  };

  // Wrap checkGenerationStatus with useCallback
  const checkGenerationStatus = useCallback((requestId, aiResponseId, trackId, onComplete) => {
    const pollingInterval = 5000; // 5 seconds
    let intervalId;
    let retries = 0;
    const maxRetries = 5; // Allow a few retries on network errors before failing
    let currentKnownStatus = 'pending'; // Keep track of the status reported to parent

    const updateMessageWithError = (errorStatus = 'error') => {
      console.warn(
        `[Chunk-Generation-Status] Polling stopped due to ${errorStatus} for requestId: ${requestId}, trackId: ${trackId}. Updating UI with error.`
      );
      setLoadingResponseId(null); // Clear loading indicator
      // Update the message state to show error
      setMessages((prev) =>
        prev.map((msg) => {
          if (
            msg.sender === "Wubble" &&
            Number(msg.ai_response_id) === Number(aiResponseId)
          ) {
            // Keep existing message data, but mark as error
            return {
              ...msg,
              is_error: true, // Mark the entire message as error if any track fails? Or handle per-track?
              // For now, marking the whole message. AIChatBox will render individual track errors.
              error_message: `Track ${trackId + 1} failed. ${msg.error_message || 'Please try again.'}`, // Append track info
              text: msg.text || `Track ${trackId + 1} generation failed. Please try again.`, // Update text if needed
            };
          }
          return msg;
        })
      );
      // ... rest of existing error handling (version history, current song details, stopAudio) ...
      // Clear current song details if it matches the failed generation
      if (localState.currentSongDetails?.ai_response_id === aiResponseId) {
        updateLocalState("currentSongDetails", null);
      }
      updateLocalState("responseInProgress", false);
      stopAudio(); // Stop audio if playing
    };


    const pollStatus = async () => {
      const token = getCookie("token") || getPartitionedLocalStorage("token");
      // --- EDIT 1: Add check if polling should continue ---
      // If the component determined the status is already complete or error, stop polling early.
      if (currentKnownStatus === 'complete' || currentKnownStatus === 'error') {
        //console.log(`[Chunk-Generation-Status] Polling stopped early for ${requestId}/${trackId} as status is already ${currentKnownStatus}`);
        clearInterval(intervalId);
        return;
      }
      // --- END EDIT 1 ---

      try {
        //console.log(`[Chunk-Generation-Status] Polling for ${requestId}/${trackId}. Current known status: ${currentKnownStatus}`); // Add log
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_ENDPOINT}/api/chunk-generation-status/${requestId}/${trackId}`, // updated path
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const { status } = response.data; // Assuming backend returns 'pending', 'streaming', 'completed', 'error'
        retries = 0; // Reset retries on successful poll

        //console.log(`[Chunk-Generation-Status] Received status: ${status} for ${requestId}/${trackId}`); // Log received status        // --- EDIT 2: Handle different statuses from backend ---
        if (status === "completed") {
          posthog.capture("Music Generation Failed", {
            is_user_logged_in: true
          });

          // Removed posthog event capture - this is now handled in AIChatBox component
          
          //console.log(
          //  `[Chunk-Generation-Status] Generation completed for requestId: ${requestId} and trackId: ${trackId}`
          //);
          clearInterval(intervalId);
          if (currentKnownStatus !== 'complete') { // Only notify if status changed
            onComplete('complete'); // Notify parent with 'complete' status
            currentKnownStatus = 'complete';
          }
        } else if (status === "error") {

          clearInterval(intervalId);
          if (currentKnownStatus !== 'error') { // Only notify if status changed
            onComplete('error'); // Notify parent with 'error' status
            currentKnownStatus = 'error';
            // updateMessageWithError('error'); // Call error handler immediately
          }
        } else if (status === "streaming" || status === "processing") { // Handle streaming/processing state
          //console.log(`[Chunk-Generation-Status] Generation streaming/processing for ${requestId}/${trackId}`);
          if (currentKnownStatus !== 'streaming') { // Only notify if status changed from pending
            onComplete('streaming');
            currentKnownStatus = 'streaming';
          }
          // Continue polling
        } else if (status === "pending") {
          //console.log(`[Chunk-Generation-Status] Generation still pending for ${requestId}/${trackId}`);
          // If status is still 'pending', continue polling. No need to call onComplete.
          // We could potentially implement a timeout for the pending state here.
        } else {
          //console.warn(`[Chunk-Generation-Status] Unknown status received: ${status} for ${requestId}/${trackId}`);
          // Handle unknown status? Maybe treat as error after retries or log.
          // Continue polling for now.
        }
        // --- END EDIT 2 ---

      } catch (error) {
        Sentry.captureException(error);
        retries++;
        //console.error(
        //  `[Chunk-Generation-Status] Error polling status for requestId: ${requestId}, trackId: ${trackId}. Retry ${retries}/${maxRetries}. Error: ${error.message}`
        //);
        if (retries >= maxRetries || error.response?.status === 404) {
          //console.warn(`[Chunk-Generation-Status] Max retries reached or 404 for ${requestId}/${trackId}. Assuming error.`);
          clearInterval(intervalId);
          if (currentKnownStatus !== 'error') { // Only notify if status changed
            onComplete('error'); // Notify parent with 'error' status after max retries/404
            currentKnownStatus = 'error';
            // updateMessageWithError('error'); // Call error handler
          }
        }
        // Otherwise, continue polling after a network error (up to maxRetries)
      }
    };

    // Start polling immediately
    pollStatus();
    intervalId = setInterval(pollStatus, pollingInterval);

    // Return a cleanup function for the effect in AIChatBox to call if needed
    return () => {
      //console.log(`[ChatInterface Cleanup] Clearing interval for ${requestId}/${trackId}`);
      clearInterval(intervalId);
    };
    // --- EDIT 3: Add dependencies used inside updateMessageWithError ---
  }, [stopAudio, localState.versionHistory, updateLocalState, localState.currentSongDetails, setMessages, setLoadingResponseId]);
  // --- END EDIT 3 ---

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform);
  const isPowerPointEnvironment = () => {
    return window.Office && window.Office.context && window.Office.context.platform;
  };
  const isMacPowerPoint = isMac && isPowerPointEnvironment();
  // Treat Mac PowerPoint like Safari for streaming audio compatibility
  const shouldUseSafariMode = isSafari || isMacPowerPoint;

  const playAudioWithLoadingIndicator = (
    audioStreamUrl,
    title,
    description,
    avatar,
    aiResponseId,
    duration
  ) => {
    setLoadingResponseId(aiResponseId);
    setStreamingResponseId(aiResponseId);
    setStreamingSrc(audioStreamUrl);
    setAiResponseId(aiResponseId);
    setIsBuffering(true);

    const timeoutMs = 40000; // Timeout for first chunk
    const controller = new AbortController();
    streamingControllerRef.current = controller;
    const signal = controller.signal;

    let firstChunkReceived = false;
    const timeoutHandle = setTimeout(() => {
      if (!firstChunkReceived) {
        controller.abort();
        setToastMessage("Streaming timed out. Please try regenerating.");
        updateLocalState("responseInProgress", false);
      }
    }, timeoutMs);

    const safariStreamUrl = shouldUseSafariMode
      ? `${audioStreamUrl}&format=mp3`
      : audioStreamUrl;

    fetch(safariStreamUrl, {
      signal,
      headers: {
        Authorization: `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`,
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const reader = response.body.getReader();
        let chunkCounter = 0;

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            clearTimeout(timeoutHandle);
            setStreamingResponseId(null);
            setStreamingSrc(null);
            setIsBuffering(false);
            updateLocalState("responseInProgress", false);
            break;
          }

          if (value) {
            chunkCounter++;

            let chunkCondition = shouldUseSafariMode
              ? chunkCounter >= 10
              : chunkCounter >= 8;

            // First chunk logic
            if (
              !firstChunkReceived &&
              chunkCondition &&
              location.pathname === "/chatPage"
            ) {
              firstChunkReceived = true;
              clearTimeout(timeoutHandle);

              updateLocalState("generatedRequestAudio", true);
              setLoadingResponseId(null);
              scrollToBottom();
              setDiscoverId(null);

              if (!shouldUseSafariMode) {
                playAudio(
                  safariStreamUrl,
                  title,
                  description,
                  avatar,
                  duration
                );
              } else {
                console.log(`[ChatInterface] ${isMacPowerPoint ? 'Mac PowerPoint' : 'Safari'}: Skipping auto-play, waiting for user interaction`);
              }
              if (pageWidth >= 768) {
                updateLocalState("isDetailsPanelOpen", true);
              }
              continue;
            }
          }
        }
      })
      .catch((error) => {
        clearTimeout(timeoutHandle);
        Sentry.captureException(error);
        if (error.name === "AbortError" && stopGenerationRef.current) {
          // console.log("Generation stopped by user.");
          setMessages((prev) =>
            prev.map((msg) => {
              if (
                msg.sender === "Wubble" &&
                Number(msg.ai_response_id) === Number(aiResponseId)
              ) {
                return {
                  ...msg,
                  isStopped: true,
                  text: "You have stopped the generation! Re-enter prompt to start generating again",
                };
              }
              return msg;
            })
          );

          // Update version history
          if (localState?.versionHistory) {
            const updatedVersionHistory = localState?.versionHistory.map(
              (group) => ({
                ...group,
                versions: group.versions.map((v) =>
                  v.ai_response_id === aiResponseId
                    ? {
                      ...v,
                      isStopped: true,
                      text: "You have stopped the generation! Re-enter prompt to start generating again",
                    }
                    : v
                ),
              })
            );

            updateLocalState("versionHistory", updatedVersionHistory);
          }

          // Clear current song details if it matches the failed generation
          updateLocalState("currentSongDetails", null);

          // Close audio player
          stopAudio();
        } else {
          console.error("Error streaming audio:", error);
          setToastMessage("An error occurred while streaming audio.");
          handleStreamingError(aiResponseId, error.message);
        }

        setLoadingResponseId(null);
        setStreamingResponseId(null);
        setStreamingSrc(null);
        setIsBuffering(false);
        updateLocalState("responseInProgress", false);
      });
  };

  const handleStreamingError = (aiResponseId, errorMessage) => {
    // Find the AI response in the messages
    setMessages((prevMessages) =>
      prevMessages.map((msg) => {
        if (msg.ai_response_id === aiResponseId) {
          return {
            ...msg,
            is_error: true,
            error_message: "Streaming timed out. Please try again.",
            text: "Streaming timed out. Please try again.",
          };
        }
        return msg;
      })
    );

    // Update version history
    if (localState?.versionHistory) {
      const updatedVersionHistory = localState.versionHistory
        .map((group) => ({
          ...group,
          versions: group.versions.filter(
            (v) => v.ai_response_id !== aiResponseId
          ),
        }))
        .filter((group) => group.versions.length > 0);

      updateLocalState("versionHistory", updatedVersionHistory);
    }

    // Clear current song details if it matches the failed generation
    updateLocalState("currentSongDetails", null);

    // Close audio player
    stopAudio();

    // Optionally, show a toast message
    setToastMessage("Streaming timed out. Please try again.");
    setToastType("error");

    // Scroll to the updated message
    scrollToBottom();
  };

  const handleGenerationError = (aiResponseId, errorMessage) => {
    setMessages((prevMessages) =>
      prevMessages.map((msg) => {
        if (Number(msg.ai_response_id) === Number(aiResponseId)) {
          return {
            ...msg,
            is_error: true,
            error_message: errorMessage,
            text: "Generation failed. Please try again.",
          };
        }
        return msg;
      })
    );

    // Update version history
    if (localState.versionHistory) {
      const updatedVersionHistory = localState.versionHistory
        .map((group) => ({
          ...group,
          versions: group.versions.filter(
            (v) => Number(v.ai_response_id) !== Number(aiResponseId)
          ),
        }))
        .filter((group) => group.versions.length > 0);

      updateLocalState("versionHistory", updatedVersionHistory);
    }

    // Clear current song details if it matches the failed generation
    if (localState.currentSongDetails?.ai_response_id === aiResponseId) {
      updateLocalState("currentSongDetails", null);
    }

    // Close audio player
    stopAudio();

    setToastMessage(errorMessage);
    setToastType("error");
  };

  const handleRegenerate = (aiResponseId) => {
    if (localState?.userProfileData?.remaining_credits == 0) {
      if (localState.isAdmin && localState.isTeamMember) {
        dispatch(setRedirectAfterSelectedPlan("/create-team"));
        dispatch(setSubscriptionModal(true));
        dispatch(setSubscriptionTypeModal("upgrade"));
      } else if (!localState.isTeamMember) {
        dispatch(setRedirectAfterSelectedPlan("/create-team"));
        dispatch(setSubscriptionModal(true));
      }
      return;
    }

    if (currentRequestId === aiResponseId) {
      setToastMessage("Request already in progress");
      setToastType("warning");
      return;
    }

    // console.log("Regenerating response with ID:", aiResponseId);
    posthog.capture("Click on Regenerate Button", {
      is_user_logged_in: true
    });

    // Ensure no duplicate requests
    if (currentRequestId) {
      setToastMessage("Request already in progress");
      setToastType("warning");
      return;
    }

    // Proceed with regeneration if not already in progress
    if (!isRegeneration) {
      // posthog.capture("Regenerate AI Response");
      setIsRegeneration(true);
      setRegeneratingResponseId(aiResponseId);
      setBeforeRegenerateLoaderId(aiResponseId);

      // Generate a new requestId
      const requestId = `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 11)}`;

      // Create FormData for regeneration request
      const formData = new FormData();

      Object.keys(lastUserInput).forEach((key) => {
        formData.append(key, lastUserInput[key]);
      });

      if (lastUserInput.file?.url) {
        const mediaFileType = lastUserInput.file?.type?.split("/")[0];
        const urlsObject = {
          audios: mediaFileType === "audio" ? lastUserInput.file.url : [],
          images: mediaFileType === "image" ? lastUserInput.file.url : [],
          videos: mediaFileType === "video" ? lastUserInput.file.url : [],
        };
        formData.append("mediaUrls", JSON.stringify(urlsObject));
      }

      // Append additional fields
      formData.append("message_id", lastUserInput.id);
      formData.append("requestId", requestId);
      formData.append("regeneration", true);
      formData.append("regeneratingResponseId", aiResponseId);

      // Update state and send request
      updateLocalState("responseInProgress", true);
      sendAiResponseRequest(formData);

      // Reset regeneration state
      setIsRegeneration(false);
      setRegeneratingResponseId(null);
    }
  };

  const handleErrorRegenerate = async (aiResponseId) => {
    if (!lastUserInput) {
      setToastMessage("Unable to retry. Please enter a new prompt.");
      return;
    }

    // Generate a new requestId
    const requestId = `${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 11)}`;

    setIsRegeneration(true);
    setRegeneratingResponseId(aiResponseId);
    setBeforeRegenerateLoaderId(aiResponseId);

    const formData = new FormData();
    formData.append("message_id", lastUserInput.id);
    formData.append("requestId", requestId);
    formData.append("regeneration", true);
    formData.append("regeneratingResponseId", aiResponseId);
    formData.append("isErrorRetry", true);

    Object.keys(lastUserInput).forEach((key) => {
      formData.append(key, lastUserInput[key]);
    });

    if (lastUserInput.file?.url) {
      const mediaFileType = lastUserInput.file?.type?.split("/")[0];
      const urlsObject = {
        audios: mediaFileType === "audio" ? lastUserInput.file.url : [],
        images: mediaFileType === "image" ? lastUserInput.file.url : [],
        videos: mediaFileType === "video" ? lastUserInput.file.url : [],
      };
      formData.append("mediaUrls", JSON.stringify(urlsObject));
    }

    updateLocalState("responseInProgress", true);
    sendAiResponseRequest(formData);
    setIsRegeneration(false);
    setRegeneratingResponseId(null);
  };

  const handleBlankRegenerate = (aiResponseId, requestId) => {
    // console.log("Regenerating response with ID:", aiResponseId);

    if (!lastUserInput) {
      setToastMessage("Unable to retry. Please enter a new prompt.");
      return;
    }
    setIsRegeneration(true);
    setRegeneratingResponseId(aiResponseId);
    setBeforeRegenerateLoaderId(aiResponseId);
    setCurrentRequestId(requestId);

    const formData = new FormData();

    // Add all required fields that match the backend route requirements
    formData.append("message_id", lastUserInput.id);
    formData.append("requestId", requestId);
    formData.append("regeneration", true);
    formData.append("regeneratingResponseId", aiResponseId);

    Object.keys(lastUserInput).forEach((key) => {
      formData.append(key, lastUserInput[key]);
    });

    // // Handle media if present in lastUserInput
    // if (lastUserInput.file?.content) {
    //   const fileBuffer = new Uint8Array(lastUserInput.file.content.data);
    //   const fileBlob = new Blob([fileBuffer], {
    //     type: lastUserInput.file.type,
    //   });
    //   formData.append("media", fileBlob, lastUserInput.file.name);
    // }

    if (lastUserInput.file?.url) {
      const mediaFileType = lastUserInput.file?.type?.split("/")[0];
      const urlsObject = {
        audios: mediaFileType === "audio" ? lastUserInput.file.url : [],
        images: mediaFileType === "image" ? lastUserInput.file.url : [],
        videos: mediaFileType === "video" ? lastUserInput.file.url : [],
      };
      formData.append("mediaUrls", JSON.stringify(urlsObject));
    }

    updateLocalState("responseInProgress", true);
    sendAiResponseRequest(formData);
    setIsRegeneration(false);
    setRegeneratingResponseId(null);
  };

  // Group related AI responses based on parent_response_id
  const groupRelatedMessages = (messages) => {
    const responseMap = {};

    // Step 1: Group AI messages by their parent_response_id or ai_response_id
    messages.forEach((msg) => {
      if (msg.sender === "Wubble") {
        const groupId =
          msg.parent_response_id && !msg.is_incomplete
            ? msg.parent_response_id + "sksres"
            : msg.ai_response_id;

        if (!responseMap[groupId]) {
          responseMap[groupId] = [];
        }

        responseMap[groupId].push(msg);
      }
    });

    // Step 2: Consolidate the grouped messages, while maintaining order
    const groupedMessages = messages.reduce((acc, msg) => {
      if (msg.sender === "User") {
        acc.push(msg); // Directly add user messages without grouping
      } else if (msg.sender === "Wubble") {
        const groupId =
          msg.parent_response_id && !msg.is_incomplete
            ? msg.parent_response_id + "sksres"
            : msg.ai_response_id;
        if (responseMap[groupId] && responseMap[groupId].length > 0) {
          acc.push(responseMap[groupId]);
          delete responseMap[groupId]; // Prevent re-adding the same group
        }
      }
      return acc;
    }, []);

    // console.log("Grouped Messages:", messages);

    // console.log("Messages:", messages);
    return groupedMessages;
  };

  const groupedMessages = groupRelatedMessages(messages);

  // --- EDIT 1: Add callback to handle loading completion ---
  const handleLoadingComplete = useCallback((completedResponseId) => {
    console.log(`[ChatInterface] Received loading complete signal for ${completedResponseId}`);
    // Only clear global states if the completed ID matches the one we are tracking
    if (loadingResponseId === completedResponseId) {
      console.log(`[ChatInterface] Clearing global loading states for ${completedResponseId}`);
      setLoadingResponseId(null);
      updateLocalState("responseInProgress", false);
      // Note: setIsBuffering/setStreamingSrc are now called directly from AIChatBox completion effect
    }
  }, [loadingResponseId, updateLocalState, setLoadingResponseId]); // Add setLoadingResponseId dependency
  // --- END EDIT 1 ---

  return (
    <>
      <div
        className={`flex-1 flex flex-col ${isAudioPlaying
          ? `pb-12`
          : `pb-8`
          } scrollable-content overflow-y-auto items-center justify-top w-full pb-12`}
        style={{
          maxHeight: `calc(100vh - ${isAudioPlaying ? '19rem' : '14.5rem'} - ${scrollHeight / 10}rem)`
        }}
      >
        {groupedMessages.map((groupOrMessage, index) => {
          if (Array.isArray(groupOrMessage)) {
            // AI Message Group
            const mainMessage = groupOrMessage[0];
            const relatedMessages = groupOrMessage?.slice(1);
            const isLastMessage =
              index === groupedMessages.length - 1 &&
              groupOrMessage.includes(messages[messages.length - 1]);

            return (
              <AIChatBox
                key={mainMessage.ai_response_id}
                message={mainMessage}
                isLastMessage={isLastMessage}
                loadingResponseId={loadingResponseId}
                beforeRegenerateLoaderId={beforeRegenerateLoaderId}
                onRegenerate={handleRegenerate}
                relatedMessages={relatedMessages}
                id={mainMessage.ai_response_id}
                passedFromChatInterface={true}
                onErrorRegenerate={handleErrorRegenerate}
                onBlankRegenerate={handleBlankRegenerate}
                projectName={projectName}
                generationCount={generationCount}
                projectId={projectId}
                onGenerationError={handleGenerationError}
                checkGenerationStatus={checkGenerationStatus}
                setTriggerTaskModal={setTriggerTaskModal}
                onLoadingComplete={handleLoadingComplete}
              />
            );
          } else {
            // User Message
            return <UserChatBox key={index} message={groupOrMessage} />;
          }
        })}
        {beforeResponseLoader && (
          <div className="flex-1 mt-auto flex items-center w-full max-w-screen-lg lg:max-w-screen-md">
            <ChatBoxLoader className={"w-full"} giminiResponseLoader={true} />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* {showStreakModal && <StreakModal
        data={streakInfo}
        onClose={() => setShowStreakModal(false)}
      />} */}
      {toastMessage && (
        <Toast
          type={toastType}
          message={toastMessage}
          onClose={() => setToastMessage(null)}
        />
      )}
      {/* Ensure TaskCompletion is rendered here, controlled by local state */}
      {triggerTaskModal && (<TaskCompletion trigger={triggerTaskModal} />)}
    </>
  );
};

export default ChatInterface;
